import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { OrthographicCamera, OrbitControls } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { IS_IOS } from '../config';

// BuildView — the plan view you lay a city out in.
//
// Straight down at 90°, no fog, no sky, no time of day. Everything the world
// does to look good is working against you here: fog eats the far end of the
// map, a low sun throws long shadows that read as geometry, and the hour decides
// how much you can see at all. A build view answers a different question — where
// is this, how big is it, does it line up — so it drops all of that.
//
// ORTHOGRAPHIC, which is the part that matters. Under perspective, buildings
// away from the centre lean outward and their roofs shift off their footprints,
// so a tower at the edge of the frame reads as standing somewhere it is not.
// That is fine for a view and useless for a measurement: you cannot judge
// whether a paste lines up with a grid cell when the projection itself is
// sliding the evidence. Orthographic has no vanishing point, so a roof sits
// exactly over its plot and one cell is the same size everywhere on screen.
//
// Rotation is off on purpose. Tilting a plan view is how you stop being able to
// trust it, and the moment it is off-axis the alignment you were checking is
// unverifiable again. Pan and zoom only.

// NAVIGATES LIKE A MAP, not like an orbit rig.
//
// OrbitControls puts pan on the RIGHT button because it assumes the left one is
// rotating. With rotation off, that leaves the left button doing nothing at all
// — you drag across the map and it sits there. So the buttons are rebound to
// what a top-down view implies: drag to pan, wheel to zoom.
const MOUSE = { LEFT: THREE.MOUSE.PAN, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
// iOS Safari crashes on a pinch-dolly over the canvas (see config.IS_IOS), so
// there two fingers pan rather than zoom — the same trade the street camera makes.
const TOUCH = { ONE: THREE.TOUCH.PAN, TWO: IS_IOS ? THREE.TOUCH.PAN : THREE.TOUCH.DOLLY_PAN };

export default function BuildView() {
  const bounds = useWorldStore((s) => s.cityBounds);
  const size = useThree((s) => s.size);
  const camRef = useRef(null);
  const ctlRef = useRef(null);
  // Once you have moved the camera it is YOURS. Auto-framing exists to give a
  // sensible first frame, not to keep yanking you back to the middle of the map
  // every time something in the scene re-renders.
  const moved = useRef(false);

  const view = useMemo(() => {
    const w = bounds ? Math.max(1, bounds.max[0] - bounds.min[0]) : 240;
    const d = bounds ? Math.max(1, bounds.max[1] - bounds.min[1]) : 240;
    const centre = bounds
      ? [(bounds.min[0] + bounds.max[0]) / 2, (bounds.min[1] + bounds.max[1]) / 2]
      : [0, 0];
    return {
      centre,
      // Ortho zoom is pixels-per-world-unit, so framing the map is just "fit the
      // footprint in the viewport" — the same sum on both axes, smaller wins,
      // with a margin so the edge blocks are not flush against the frame.
      zoom: Math.min(size.width / (w * 1.1), size.height / (d * 1.1)),
      // High enough that the tallest tower is still in front of the camera. An
      // orthographic camera's height does not affect framing at all — only
      // near/far clipping — so this is "above everything", not a zoom level.
      y: Math.max(400, Math.max(w, d) * 2),
    };
  }, [bounds, size.width, size.height]);

  // FRAMED IMPERATIVELY, NOT THROUGH PROPS.
  //
  // The obvious version passes position={[cx, y, cz]} and zoom={z} to the
  // camera. That quietly breaks navigation: the array literal is a new reference
  // on every render, so R3F re-applies it whenever anything re-renders this
  // component — and re-applying position teleports the camera back to the middle
  // of the map, undoing the pan you were in the middle of. The controls and the
  // props end up fighting over the same values, and the props win on every
  // unrelated state change in the scene.
  //
  // So the camera is positioned once, by hand, and afterwards belongs to
  // OrbitControls alone.
  const frame = useCallback(() => {
    const cam = camRef.current;
    const ctl = ctlRef.current;
    if (!cam || !ctl) return;
    cam.position.set(view.centre[0], view.y, view.centre[1]);
    cam.zoom = view.zoom;
    cam.near = 0.1;
    cam.far = view.y * 2.5;
    cam.updateProjectionMatrix();
    ctl.target.set(view.centre[0], 0, view.centre[1]);
    ctl.update();
  }, [view]);

  // Re-fit while the view is still untouched — so the map lands framed when the
  // city finally reports its bounds, and re-fits on a window resize. Stops the
  // moment you take control.
  useLayoutEffect(() => { if (!moved.current) frame(); }, [frame]);

  // F re-frames on demand, which is the way back once you are lost at 40x.
  useLayoutEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'f' && e.key !== 'F') return;
      if (/^(INPUT|TEXTAREA)$/.test(e.target?.tagName) || e.target?.isContentEditable) return;
      moved.current = false;
      frame();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [frame]);

  return (
    <>
      <OrthographicCamera ref={camRef} makeDefault />
      <OrbitControls
        ref={ctlRef}
        makeDefault
        onStart={() => { moved.current = true; }}
        // Straight down, and it stays there. Both polar limits at 0 pin the
        // camera overhead; three's own makeSafe() nudges it off the pole by a
        // millionth of a radian, so there is no degenerate up-vector to handle.
        minPolarAngle={0}
        maxPolarAngle={0}
        enableRotate={false}
        enablePan
        // Pan in the SCREEN plane. The default pans along the ground plane
        // relative to the camera's orientation, which for a camera pointing
        // straight down is a coin-flip about which way "up" drags.
        screenSpacePanning
        enableZoom
        enableDamping
        dampingFactor={0.12}
        panSpeed={1.1}
        zoomSpeed={1.1}
        mouseButtons={MOUSE}
        touches={TOUCH}
        // Ortho dolly is a zoom multiplier, not a distance — clamped generously
        // so you can go from the whole map down to a single doorway.
        minZoom={view.zoom * 0.35}
        maxZoom={view.zoom * 80}
      />
      {/* FLAT AND SHADOWLESS, deliberately. The town's rig is an outdoor one
          keyed at the road-facing facades, which at any hour but midday leaves
          half the map in shadow — and a shadow reads exactly like a building
          from above. Two lights straight down instead: nothing hides, and
          nothing that is not there appears to be.
          Modest levels because the street's reflection environment stays mounted
          (PlanetScene keeps it to avoid a full shader recompile), so these are
          topping up an already-lit scene rather than lighting it alone. */}
      <ambientLight intensity={0.85} />
      <directionalLight position={[view.centre[0], view.y, view.centre[1] + 1]} intensity={0.9} castShadow={false} />
    </>
  );
}
